import { create } from "zustand";
import {
  api,
  IPurchaseSearchResponse,
  ICreateSupplyOrderRequest,
  ISupplyOrderResponse,
  IOrdersListResponse,
  getNullableString,
} from "../api/api";

export interface SupplyProduct {
  id: string;
  article: string;
  name: string;
  brand: string;
  category: string;
  sizes: {
    size: string;
    price: number;
    quantity: number;
  }[];
  images: string[];
  description?: string;
}

export interface CartItem {
  id: string; // уникальный ID для элемента корзины
  productId: string;
  article: string;
  name: string;
  brand: string;
  size: string;
  quantity: number;
  price: number;
  image: string;
  addedAt: string;
}

export interface SupplyOrder {
  id: string;
  productId: string;
  article: string;
  size: string;
  quantity: number;
  price: number;
  status: "pending" | "confirmed" | "shipped" | "delivered" | "cancelled";
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  deliveryAddress: string;
  deliveryMethod: "pickup" | "delivery";
  paymentMethod: "cash" | "card" | "transfer";
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Дополнительная информация о товаре для отображения
  productName?: string;
  productImage?: string;
  productBrand?: string;
  productColor?: string;
  totalItems?: number; // Общее количество товаров в заказе
  totalAmount?: number; // Общая сумма заказа, если есть
  items?: Array<{
    article: string;
    size_eu: number;
    price: number;
    count: number;
    model?: string;
    color?: string;
    image_url?: string;
  }>;
}

// Вспомогательная функция для генерации размеров по умолчанию
const generateDefaultSizes = () => {
  const sizes = [];
  for (let size = 3.5; size <= 17; size += 0.5) {
    sizes.push({
      size: size.toString(),
      price: Math.floor(Math.random() * 10000) + 10000, // Цена от 10000 до 20000
      quantity: Math.floor(Math.random() * 10) + 1, // Количество от 1 до 10
    });
  }
  return sizes;
};

// Константы для localStorage
const CART_STORAGE_KEY = 'legitfam_supply_cart';

// Функции для работы с localStorage
const saveCartToStorage = (cart: CartItem[]) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Ошибка сохранения корзины в localStorage:', error);
  }
};

const loadCartFromStorage = (): CartItem[] => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    return savedCart ? JSON.parse(savedCart) : [];
  } catch (error) {
    console.error('Ошибка загрузки корзины из localStorage:', error);
    return [];
  }
};

interface SupplyStoreState {
  // Состояния загрузки
  isSearching: boolean;
  isLoading: boolean;

  // Данные поиска
  searchResults: SupplyProduct[];
  searchQuery: string;

  // Корзина
  cart: CartItem[];

  // Заказы на закупку
  orders: SupplyOrder[];
  ordersSearchQuery: string;
  ordersStatusFilter: string[];
  isLoadingMore: boolean;
  hasMoreOrders: boolean;
  ordersOffset: number;
  ordersLimit: number;

  // Методы поиска
  searchProduct: (article: string) => Promise<void>;
  clearSearchResults: () => void;

  // Методы для работы с корзиной
  addToCart: (product: SupplyProduct, size: string, quantity: number) => void;
  updateCartItem: (itemId: string, quantity: number) => void;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartItemsCount: () => number;

  // Методы для работы с заказами
  createOrder: (orderData: ICreateSupplyOrderRequest) => Promise<void>;
  updateOrder: (
    orderId: string,
    updates: Partial<SupplyOrder>
  ) => Promise<void>;
  deleteOrder: (orderId: string) => Promise<void>;
  fetchOrders: () => Promise<void>;
  loadMoreOrders: () => Promise<void>;
  setOrdersSearchQuery: (query: string) => void;
  setOrdersStatusFilter: (statuses: string[]) => void;
  getFilteredOrders: () => SupplyOrder[];

  // Сброс состояния
  reset: () => void;
}

const initialState = {
  isSearching: false,
  isLoading: false,
  searchResults: [],
  searchQuery: "",
  cart: loadCartFromStorage(), // Загружаем корзину из localStorage
  orders: [],
  ordersSearchQuery: "",
  ordersStatusFilter: [],
  isLoadingMore: false,
  hasMoreOrders: true,
  ordersOffset: 0,
  ordersLimit: 5,
};

export const useSupplyStore = create<SupplyStoreState>((set, get) => ({
  ...initialState,

  searchProduct: async (article: string) => {
    set({ isSearching: true, searchQuery: article });

    try {
      // Используем реальный API для поиска по артикулу
      const response: IPurchaseSearchResponse =
        await api.searchPurchasesByArticle(article);

      // Преобразуем ответ API в формат SupplyProduct
      const searchResults: SupplyProduct[] = response.model
        ? [
            {
              id: Date.now().toString(),
              article: article,
              name: `${response.model} ${response.color}`,
              brand: response.model.split(" ")[0] || "Nike", // Берем первое слово как бренд
              category: "Кроссовки",
              sizes: response.data.map((item) => ({
                size: item.size,
                price: item.price,
                quantity: Math.floor(Math.random() * 10) + 1, // Временно генерируем случайное количество
              })),
              images: [response.photo_url],
              description: `${response.model} в цвете ${response.color}`,
            },
          ]
        : [];

      set({
        searchResults,
        isSearching: false,
      });
    } catch (error) {
      console.error("Ошибка поиска:", error);

      // В случае ошибки API, показываем fallback с моковыми данными
      const fallbackResults: SupplyProduct[] = article
        ? [
            {
              id: "1",
              article: article,
              name: "Nike Air Jordan 1 Retro High OG",
              brand: "Nike",
              category: "Sneakers",
              sizes: generateDefaultSizes(),
              images: [""],
              description: "Классические баскетбольные кроссовки",
            },
          ]
        : [];

      set({
        searchResults: fallbackResults,
        isSearching: false,
      });
    }
  },

  clearSearchResults: () => {
    set({ searchResults: [], searchQuery: "" });
  },

  // Методы для работы с корзиной
  addToCart: (product: SupplyProduct, size: string, quantity: number) => {
    const sizeInfo = product.sizes.find(s => s.size === size);
    if (!sizeInfo) return;

    const cartItemId = `${product.id}_${size}`;
    
    set((state) => {
      // Проверяем, есть ли уже такой товар с таким размером в корзине
      const existingItemIndex = state.cart.findIndex(item => item.id === cartItemId);
      
      let updatedCart: CartItem[];
      
      if (existingItemIndex !== -1) {
        // Если товар уже есть, обновляем количество
        updatedCart = [...state.cart];
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          quantity: updatedCart[existingItemIndex].quantity + quantity
        };
      } else {
        // Если товара нет, добавляем новый
        const newCartItem: CartItem = {
          id: cartItemId,
          productId: product.id,
          article: product.article,
          name: product.name,
          brand: product.brand,
          size: size,
          quantity: quantity,
          price: sizeInfo.price,
          image: product.images[0] || '',
          addedAt: new Date().toISOString()
        };
        updatedCart = [...state.cart, newCartItem];
      }
      
      // Сохраняем корзину в localStorage
      saveCartToStorage(updatedCart);
      
      return { cart: updatedCart };
    });
  },

  updateCartItem: (itemId: string, quantity: number) => {
    set((state) => {
      let updatedCart: CartItem[];
      
      if (quantity <= 0) {
        // Если количество 0 или меньше, удаляем товар
        updatedCart = state.cart.filter(item => item.id !== itemId);
      } else {
        // Обновляем количество
        updatedCart = state.cart.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        );
      }
      
      // Сохраняем корзину в localStorage
      saveCartToStorage(updatedCart);
      
      return { cart: updatedCart };
    });
  },

  removeFromCart: (itemId: string) => {
    set((state) => {
      const updatedCart = state.cart.filter(item => item.id !== itemId);
      
      // Сохраняем корзину в localStorage
      saveCartToStorage(updatedCart);
      
      return { cart: updatedCart };
    });
  },

  clearCart: () => {
    // Очищаем localStorage
    saveCartToStorage([]);
    
    set({ cart: [] });
  },

  getCartTotal: () => {
    const { cart } = get();
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  },

  getCartItemsCount: () => {
    const { cart } = get();
    return cart.reduce((total, item) => total + item.quantity, 0);
  },

  createOrder: async (orderData: ICreateSupplyOrderRequest) => {
    set({ isLoading: true });

    try {
      // Используем реальный API для создания заказа
      const response: ISupplyOrderResponse = await api.createSupplyOrder(
        orderData
      );

      set((state) => ({
        orders: [...state.orders, response],
        isLoading: false,
      }));
    } catch (error) {
      console.error("Ошибка создания заказа:", error);

      // В случае ошибки API, создаем мок заказ
      const fallbackOrder: SupplyOrder = {
        id: Date.now().toString(),
        productId: orderData.productId,
        article: orderData.article,
        size: orderData.size,
        quantity: orderData.quantity,
        price: orderData.price,
        status: "pending",
        customerName: orderData.customerName,
        customerPhone: orderData.customerPhone,
        customerEmail: orderData.customerEmail,
        deliveryAddress: orderData.deliveryAddress,
        deliveryMethod: orderData.deliveryMethod,
        paymentMethod: orderData.paymentMethod,
        notes: orderData.notes,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      set((state) => ({
        orders: [...state.orders, fallbackOrder],
        isLoading: false,
      }));
    }
  },

  updateOrder: async (orderId: string, updates: Partial<SupplyOrder>) => {
    set({ isLoading: true });

    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      set((state) => ({
        orders: state.orders.map((order) =>
          order.id === orderId
            ? { ...order, ...updates, updatedAt: new Date().toISOString() }
            : order
        ),
        isLoading: false,
      }));
    } catch (error) {
      console.error("Ошибка обновления заказа:", error);
      set({ isLoading: false });
    }
  },

  deleteOrder: async (orderId: string) => {
    set({ isLoading: true });

    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      set((state) => ({
        orders: state.orders.filter((order) => order.id !== orderId),
        isLoading: false,
      }));
    } catch (error) {
      console.error("Ошибка удаления заказа:", error);
      set({ isLoading: false });
    }
  },

  fetchOrders: async () => {
    set({ isLoading: true, ordersOffset: 0 });

    try {
      // Загружаем первую страницу заказов с API
      const { ordersLimit } = get();
      const apiRes: IOrdersListResponse = await api.getOrdersList(ordersLimit, 0);

      const mapped: SupplyOrder[] = (apiRes.orders || []).map((o) => {
        const totalItems =
          o.items?.reduce((sum, it) => sum + (it.count || 0), 0) || 0;
        const totalAmount =
          o.items?.reduce(
            (sum, it) => sum + (it.price || 0) * (it.count || 0),
            0
          ) || 0;
        const first = o.items?.[0];

        // Карта соответствий статусов на русском
        const statusMapRu: Record<string, SupplyOrder["status"]> = {
          "в ожидании": "pending",
          подтвержден: "confirmed",
          отправлен: "shipped",
          доставлен: "delivered",
          отменен: "cancelled",
        };
        const normalizedStatus =
          statusMapRu[(o.order_status || "").toLowerCase()] ?? "pending";

        return {
          id: String(o.order_id),
          productId: first ? first.article : "",
          article: first ? first.article : "",
          size: first ? String(first.size_eu) : "",
          quantity: totalItems || (first ? first.count : 0),
          price: first ? first.price : 0,
          status: normalizedStatus,
          customerName: [
            getNullableString(o.address?.last_name || { String: "", Valid: false }),
            getNullableString(o.address?.first_name || { String: "", Valid: false }),
            getNullableString(o.address?.middle_name || { String: "", Valid: false }),
          ]
            .filter(Boolean)
            .join(" ")
            .trim(),
          customerPhone: "",
          deliveryAddress: o.address?.address || "",
          deliveryMethod: "delivery",
          paymentMethod: "card",
          createdAt: o.create_time || new Date().toISOString(),
          updatedAt: o.create_time || new Date().toISOString(),
          productName: first ? first.article : undefined,
          productImage: undefined,
          productBrand: undefined,
          productColor: undefined,
          totalItems,
          totalAmount,
          items: o.items || [],
        };
      });

      set({
        orders: mapped,
        isLoading: false,
        ordersOffset: ordersLimit,
        hasMoreOrders: mapped.length === ordersLimit,
      });
    } catch (error) {
      console.error("Ошибка загрузки заказов:", error);
      set({
        orders: [],
        isLoading: false,
        hasMoreOrders: false,
      });
    }
  },

  loadMoreOrders: async () => {
    const { isLoadingMore, hasMoreOrders, ordersOffset, ordersLimit } = get();
    
    if (isLoadingMore || !hasMoreOrders) return;

    set({ isLoadingMore: true });

    try {
      // Загружаем следующую страницу заказов
      const apiRes: IOrdersListResponse = await api.getOrdersList(ordersLimit, ordersOffset);

      const mapped: SupplyOrder[] = (apiRes.orders || []).map((o) => {
        const totalItems =
          o.items?.reduce((sum, it) => sum + (it.count || 0), 0) || 0;
        const totalAmount =
          o.items?.reduce(
            (sum, it) => sum + (it.price || 0) * (it.count || 0),
            0
          ) || 0;

        const first = o.items?.[0];

        const statusMapRu: Record<string, SupplyOrder["status"]> = {
          "в ожидании": "pending",
          "подтвержден": "confirmed",
          "отправлен": "shipped",
          "доставлен": "delivered",
          "отменен": "cancelled",
        };
        const normalizedStatus =
          statusMapRu[(o.order_status || "").toLowerCase()] ?? "pending";

        return {
          id: String(o.order_id),
          productId: first ? first.article : "",
          article: first ? first.article : "",
          size: first ? String(first.size_eu) : "",
          quantity: totalItems || (first ? first.count : 0),
          price: first ? first.price : 0,
          status: normalizedStatus,
          customerName: [
            getNullableString(o.address?.last_name || { String: "", Valid: false }),
            getNullableString(o.address?.first_name || { String: "", Valid: false }),
            getNullableString(o.address?.middle_name || { String: "", Valid: false }),
          ]
            .filter(Boolean)
            .join(" ")
            .trim(),
          customerPhone: "",
          deliveryAddress: o.address?.address || "",
          deliveryMethod: "delivery",
          paymentMethod: "card",
          createdAt: o.create_time || new Date().toISOString(),
          updatedAt: o.create_time || new Date().toISOString(),
          productName: first ? first.article : undefined,
          productImage: undefined,
          productBrand: undefined,
          productColor: undefined,
          totalItems,
          totalAmount,
          items: o.items || [],
        };
      });

      const { orders } = get();
      set({
        orders: [...orders, ...mapped],
        isLoadingMore: false,
        ordersOffset: ordersOffset + ordersLimit,
        hasMoreOrders: mapped.length === ordersLimit,
      });
    } catch (error) {
      console.error("Ошибка загрузки дополнительных заказов:", error);
      set({
        isLoadingMore: false,
      });
    }
  },

  reset: () => {
    // Очищаем localStorage при сбросе
    saveCartToStorage([]);
    
    set(initialState);
  },

  setOrdersSearchQuery: (query: string) => {
    set({ ordersSearchQuery: query });
  },

  setOrdersStatusFilter: (statuses: string[]) => {
    set({ ordersStatusFilter: statuses });
  },

  getFilteredOrders: () => {
    const { orders, ordersSearchQuery, ordersStatusFilter } = get();

    return orders.filter((order) => {
      // Фильтр по поисковому запросу
      const matchesSearch =
        !ordersSearchQuery ||
        order.id.toLowerCase().includes(ordersSearchQuery.toLowerCase()) ||
        order.article.toLowerCase().includes(ordersSearchQuery.toLowerCase());

      // Фильтр по статусу
      const matchesStatus =
        ordersStatusFilter.length === 0 ||
        ordersStatusFilter.includes(order.status);

      return matchesSearch && matchesStatus;
    });
  },
}));

export default useSupplyStore;
